import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";

export const NotificationBell = () => {
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const { t, lang } = useLang();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const res = await api.get("/notifications");
      setData(res.data);
    } catch {}
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const markAllRead = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" data-testid="notification-bell" className="relative h-9 w-9">
          <Bell className="h-4 w-4" />
          {data.unread > 0 && (
            <span data-testid="notification-unread-badge" className="absolute top-1 right-1 h-4 min-w-4 px-0.5 rounded-full bg-accent text-accent-foreground text-[10px] font-bold flex items-center justify-center">
              {data.unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <p className="font-heading font-semibold text-sm">{t("notifications")}</p>
          {data.unread > 0 && (
            <button data-testid="mark-all-read-btn" onClick={markAllRead} className="text-xs text-accent hover:underline">
              {t("mark_all_read")}
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto" data-testid="notification-list">
          {data.notifications.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">{t("no_notifications")}</p>
          ) : (
            data.notifications.map((n) => (
              <button
                key={n.id}
                data-testid="notification-item"
                onClick={() => n.link && navigate(n.link)}
                className={`w-full text-left px-4 py-3 border-b border-border last:border-0 hover:bg-muted transition-colors ${!n.read ? "bg-accent/5" : ""}`}
              >
                <p className="text-sm font-medium">{n.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>
                <p className="text-[10px] font-mono-data text-muted-foreground mt-1">
                  {new Date(n.created_at).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                </p>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
